import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Input as ChakraInput,
  FormControl,
  FormHelperText,
  InputGroup,
  InputProps,
  InputLeftElement,
  InputRightElement,
  useTheme,
} from '@chakra-ui/react';
import { useField } from '@unform/core';
import { isValid } from 'date-fns';
import pt from 'date-fns/locale/pt-BR';
import DateReact, {
  registerLocale,
  ReactDatePickerProps,
} from 'react-datepicker';

import { Theme } from '~/styles';

type InputElement = InputProps;

registerLocale('pt', pt);

type DatePickerProps = Omit<ReactDatePickerProps, 'onChange'>;

interface InputAddIcons extends InputElement {
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  error?: string;
}

const Input: React.FC<InputAddIcons> = ({
  name,
  iconLeft,
  iconRight,
  mb,
  error,
  ...rest
}) => {
  const { colors } = useTheme<Theme>();

  const [isFocused, setIsFocused] = useState(false);

  const handleInputFocus = useCallback(() => setIsFocused(true), []);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  return (
    <FormControl mb={mb}>
      <InputGroup>
        {iconLeft && (
          <InputLeftElement children={iconLeft} width="3rem" height="3rem" />
        )}
        <ChakraInput
          rounded="md"
          height="3rem"
          backgroundColor="gray.200"
          borderWidth="1px"
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          borderColor="gray.400"
          isInvalid={!!error && !isFocused}
          _placeholder={{
            color: '#7A7A7A',
            fontSize: '14px',
          }}
          _hover={{
            borderColor: colors.orange[500],
          }}
          _focus={{
            borderColor: colors.orange[500],
            boxShadow: `0 0 0 1px ${colors.orange[500]}`,
          }}
          errorBorderColor="red.300"
          {...rest}
        />
        {iconRight && (
          <InputRightElement children={iconRight} width="3rem" height="3rem" />
        )}
      </InputGroup>
      {!!error && (
        <FormHelperText textAlign="left" color="red.300">
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

const DatePicker: React.FC<DatePickerProps> = ({ name, ...props }) => {
  const inputRef = useRef(null);

  const { fieldName, defaultValue, error, registerField } = useField(
    name as string,
  );

  const [startDate, setStartDate] = useState(() => {
    if (isValid(defaultValue)) {
      return defaultValue;
    }

    return new Date();
  });

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      getValue: () => startDate,
      clearValue: () => setStartDate(new Date()),
      setValue: (_, value) => setStartDate(value),
    });
  }, [fieldName, registerField, startDate]);

  return (
    <DateReact
      ref={inputRef}
      customInput={<Input error={error} />}
      locale="pt"
      dateFormat="P"
      {...props}
      selected={startDate}
      onChange={date => setStartDate(date as Date)}
    />
  );
};

export default DatePicker;
